import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PaymentAdjustmentForm from './PaymentAdjustmentForm';
import '../assets/PaymentAdjustmentsList.css';

// Use production URL if on production domain, otherwise use env var or localhost
const getApiBaseUrl = () => {
  if (window.location.hostname === 'portal.jjtutorialsg.com') {
    return 'https://jjt-tutor-portal.onrender.com/api';
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
};

const API_BASE_URL = getApiBaseUrl();

const PaymentAdjustmentsList = ({ tutors }) => {
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filters - now arrays for multi-select
  const [filters, setFilters] = useState({
    tutorIds: [],
    months: [],
    years: [],
    types: []
  });
  
  // Search
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dropdown open states
  const [openDropdown, setOpenDropdown] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 20;
  
  // UI State
  const [showForm, setShowForm] = useState(false);
  const [editingAdjustment, setEditingAdjustment] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Load adjustments on mount and when filters/page change
  useEffect(() => {
    fetchAdjustments();
  }, [filters, currentPage, searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.multi-select-dropdown')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const fetchAdjustments = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage
      });

      // Add filters if set
      if (filters.tutorIds.length > 0) {
        filters.tutorIds.forEach(id => params.append('tutorId', id));
      }
      if (filters.months.length > 0) {
        filters.months.forEach(month => params.append('month', month));
      }
      if (filters.years.length > 0) {
        filters.years.forEach(year => params.append('year', year));
      }
      if (filters.types.length > 0) {
        filters.types.forEach(type => params.append('type', type));
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await axios.get(
        `${API_BASE_URL}/payment-adjustments?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setAdjustments(response.data.adjustments || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setTotalCount(response.data.pagination?.total || 0);
    } catch (err) {
      console.error('Error fetching adjustments:', err);
      setError('Failed to load payment adjustments');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterToggle = (field, value) => {
    const currentValues = filters[field];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    
    setFilters({ ...filters, [field]: newValues });
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setFilters({
      tutorIds: [],
      months: [],
      years: [],
      types: []
    });
    setSearchTerm('');
    setCurrentPage(1);
  };
  
  const toggleDropdown = (dropdown) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleAddNew = () => {
    setEditingAdjustment(null);
    setShowForm(true);
  };

  const handleEdit = (adjustment) => {
    setEditingAdjustment(adjustment);
    setShowForm(true);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingAdjustment(null);
  };

  const handleFormSubmit = async (formData) => {
    try {
      const token = localStorage.getItem('token');
      
      if (editingAdjustment) {
        // Update existing adjustment
        await axios.put(
          `${API_BASE_URL}/payment-adjustments/${editingAdjustment._id}`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      } else {
        // Create new adjustment
        await axios.post(
          `${API_BASE_URL}/payment-adjustments`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }

      // Refresh the list
      await fetchAdjustments();
      
      // Close form
      setShowForm(false);
      setEditingAdjustment(null);
    } catch (err) {
      console.error('Error saving adjustment:', err);
      throw err; // Let the form handle the error
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this payment adjustment?')) {
      return;
    }

    setDeletingId(id);

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/payment-adjustments/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Refresh the list
      await fetchAdjustments();
    } catch (err) {
      console.error('Error deleting adjustment:', err);
      setError('Failed to delete adjustment');
    } finally {
      setDeletingId(null);
    }
  };

  const formatCurrency = (amount) => {
    return `$${Math.abs(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-SG', { 
      timeZone: 'Asia/Singapore',
      day: '2-digit', 
      month: 'short', 
      year: 'numeric'
    });
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2019 + 6 }, (_, i) => 2019 + i);

  const adjustmentTypes = [
    { value: 'bonus', label: 'Bonus' },
    { value: 'deduction', label: 'Deduction' },
    { value: 'correction', label: 'Correction' },
    { value: 'one-time', label: 'One-time Payment' },
    { value: 'custom', label: 'Custom' }
  ];

  if (showForm) {
    return (
      <PaymentAdjustmentForm
        tutors={tutors}
        onSubmit={handleFormSubmit}
        onCancel={handleFormCancel}
        initialData={editingAdjustment}
        isEdit={!!editingAdjustment}
      />
    );
  }

  return (
    <div className="payment-adjustments-list">
      <div className="list-header">
        <h2>Payment Adjustments</h2>
        <button className="btn-add-new" onClick={handleAddNew}>
          + Add New Adjustment
        </button>
      </div>

      {/* Search Bar */}
      <div className="search-section">
        <input
          type="text"
          className="search-input"
          placeholder="Search by tutor name, reason, or amount..."
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-grid">
          {/* Tutor Filter */}
          <div className="filter-group">
            <label>Tutor ({filters.tutorIds.length} selected)</label>
            <div className="multi-select-dropdown">
              <button 
                className="dropdown-toggle"
                onClick={() => toggleDropdown('tutors')}
              >
                {filters.tutorIds.length === 0 ? 'All Tutors' : `${filters.tutorIds.length} selected`}
                <span className="dropdown-arrow">{openDropdown === 'tutors' ? '▲' : '▼'}</span>
              </button>
              {openDropdown === 'tutors' && (
                <div className="dropdown-menu">
                  {tutors.map(tutor => (
                    <label key={tutor._id} className="dropdown-item">
                      <input
                        type="checkbox"
                        checked={filters.tutorIds.includes(tutor._id)}
                        onChange={() => handleFilterToggle('tutorIds', tutor._id)}
                      />
                      <span>{tutor.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Month Filter */}
          <div className="filter-group">
            <label>Month ({filters.months.length} selected)</label>
            <div className="multi-select-dropdown">
              <button 
                className="dropdown-toggle"
                onClick={() => toggleDropdown('months')}
              >
                {filters.months.length === 0 ? 'All Months' : `${filters.months.length} selected`}
                <span className="dropdown-arrow">{openDropdown === 'months' ? '▲' : '▼'}</span>
              </button>
              {openDropdown === 'months' && (
                <div className="dropdown-menu">
                  {months.map(month => (
                    <label key={month.value} className="dropdown-item">
                      <input
                        type="checkbox"
                        checked={filters.months.includes(month.value)}
                        onChange={() => handleFilterToggle('months', month.value)}
                      />
                      <span>{month.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Year Filter */}
          <div className="filter-group">
            <label>Year ({filters.years.length} selected)</label>
            <div className="multi-select-dropdown">
              <button 
                className="dropdown-toggle"
                onClick={() => toggleDropdown('years')}
              >
                {filters.years.length === 0 ? 'All Years' : `${filters.years.length} selected`}
                <span className="dropdown-arrow">{openDropdown === 'years' ? '▲' : '▼'}</span>
              </button>
              {openDropdown === 'years' && (
                <div className="dropdown-menu">
                  {years.map(year => (
                    <label key={year} className="dropdown-item">
                      <input
                        type="checkbox"
                        checked={filters.years.includes(year)}
                        onChange={() => handleFilterToggle('years', year)}
                      />
                      <span>{year}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Type Filter */}
          <div className="filter-group">
            <label>Type ({filters.types.length} selected)</label>
            <div className="multi-select-dropdown">
              <button 
                className="dropdown-toggle"
                onClick={() => toggleDropdown('types')}
              >
                {filters.types.length === 0 ? 'All Types' : `${filters.types.length} selected`}
                <span className="dropdown-arrow">{openDropdown === 'types' ? '▲' : '▼'}</span>
              </button>
              {openDropdown === 'types' && (
                <div className="dropdown-menu">
                  {adjustmentTypes.map(type => (
                    <label key={type.value} className="dropdown-item">
                      <input
                        type="checkbox"
                        checked={filters.types.includes(type.value)}
                        onChange={() => handleFilterToggle('types', type.value)}
                      />
                      <span>{type.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button className="btn-clear-filters" onClick={handleClearFilters}>
            Clear All Filters
          </button>
        </div>
      </div>

      {/* Results Summary */}
      {!loading && (
        <div className="results-summary">
          Showing {adjustments.length} of {totalCount} adjustment{totalCount !== 1 ? 's' : ''}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading adjustments...</p>
        </div>
      )}

      {/* Adjustments Table */}
      {!loading && adjustments.length > 0 && (
        <div className="table-container">
          <table className="adjustments-table">
            <thead>
              <tr>
                <th>Tutor</th>
                <th>Period</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Reason</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {adjustments.map(adjustment => (
                <tr key={adjustment._id}>
                  <td className="tutor-cell">
                    <div className="tutor-name">{adjustment.tutor?.name || 'Unknown'}</div>
                    {adjustment.tutor?.email && (
                      <div className="tutor-email">{adjustment.tutor.email}</div>
                    )}
                  </td>
                  <td>
                    {months.find(m => m.value === adjustment.month)?.label} {adjustment.year}
                  </td>
                  <td>
                    <span className="type-badge">{adjustment.type}</span>
                    {adjustment.isProfitShareAdjustment && (
                      <span className="personal-badge" title="Personal adjustment — does not affect other profit sharers">Personal</span>
                    )}
                  </td>
                  <td className={adjustment.amount >= 0 ? 'amount-positive' : 'amount-negative'}>
                    {adjustment.amount >= 0 ? '+' : '-'}{formatCurrency(adjustment.amount)}
                  </td>
                  <td className="reason-cell">
                    <div className="reason-text">{adjustment.reason}</div>
                  </td>
                  <td>
                    <div className="date-text">{formatDate(adjustment.createdAt)}</div>
                    {adjustment.createdBy?.name && (
                      <div className="created-by">by {adjustment.createdBy.name}</div>
                    )}
                  </td>
                  <td className="actions-cell">
                    <button
                      className="btn-edit"
                      onClick={() => handleEdit(adjustment)}
                      disabled={deletingId === adjustment._id}
                    >
                      Edit
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleDelete(adjustment._id)}
                      disabled={deletingId === adjustment._id}
                    >
                      {deletingId === adjustment._id ? 'Deleting...' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {!loading && adjustments.length === 0 && (
        <div className="empty-state">
          <p>No payment adjustments found</p>
          <button className="btn-add-first" onClick={handleAddNew}>
            Add Your First Adjustment
          </button>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default PaymentAdjustmentsList;
